// GitHub's suggested changes: a review comment's fenced code block whose
// info string is `suggestion` holds the text that replaces the lines the
// comment targets. This module reads and writes that format and diffs a
// suggestion against those lines; nothing here renders HTML.
import { marked, type Token } from 'marked';

/** A comment body, cut at its suggestion blocks (see `splitSuggestions`). */
export type BodyPart = { kind: 'markdown'; md: string } | { kind: 'suggestion'; lines: string[] };

/** One row of a suggestion's diff; `at` indexes the row's own side (the suggestion for `add`, the original otherwise). */
export interface SuggestionRow {
  kind: 'context' | 'del' | 'add';
  text: string;
  at: number;
}

function isSuggestion(tok: Token): tok is Token & { type: 'code'; text: string } {
  return tok.type === 'code' && 'lang' in tok && typeof tok.lang === 'string' && tok.lang.trim().split(/\s+/)[0] === 'suggestion';
}

/** The lines of a suggestion's text: none for the empty text (which suggests deleting the lines). */
export function suggestionLines(text: string): string[] {
  return text === '' ? [] : text.split('\n');
}

/**
 * A body cut into Markdown and suggestion parts, in order. Only top-level
 * fenced blocks count, as on GitHub; blank Markdown between parts is dropped.
 */
export function splitSuggestions(md: string): BodyPart[] {
  const out: BodyPart[] = [];
  let pending = '';
  const flush = () => {
    // Blank lines go, but not the indentation of the first line (an indented code block).
    const text = pending.replace(/^\s*\n/, '').trimEnd();
    if (text) out.push({ kind: 'markdown', md: text });
    pending = '';
  };
  for (const tok of marked.lexer((md ?? '').replace(/\r\n?/g, '\n'))) {
    if (isSuggestion(tok)) {
      flush();
      out.push({ kind: 'suggestion', lines: suggestionLines(tok.text) });
    } else pending += tok.raw;
  }
  flush();
  return out;
}

/**
 * A comment as the composer edits it: its first suggestion block's lines,
 * and the rest of the body as text. Null when there is no suggestion block.
 */
export function parseSuggestion(md: string): { text: string; lines: string[] } | null {
  const parts = splitSuggestions(md);
  const first = parts.find((p) => p.kind === 'suggestion');
  if (!first) return null;
  const text = parts
    .filter((p) => p !== first)
    .map((p) => (p.kind === 'markdown' ? p.md : formatSuggestion('', p.lines)))
    .join('\n\n');
  return { text, lines: first.lines };
}

/**
 * The body GitHub receives for a comment with a suggestion: the text, then
 * the block. The fence is longer than any backtick run in the code, so the
 * code cannot close it.
 */
export function formatSuggestion(text: string, lines: readonly string[]): string {
  const longest = Math.max(0, ...lines.map((l) => Math.max(0, ...(l.match(/`+/g) ?? []).map((r) => r.length))));
  const fence = '`'.repeat(Math.max(3, longest + 1));
  const block = `${fence}suggestion\n${lines.map((l) => l + '\n').join('')}${fence}`;
  const lead = text.trim() ? text.trimEnd() + '\n\n' : '';
  return lead + block;
}

/**
 * The last `count` lines of one side of a review comment's `diffHunk`
 * (GitHub ends the hunk at the comment's line), i.e. the text the comment
 * targeted when it was written. Null when the hunk is missing or shorter.
 */
export function hunkLines(diffHunk: string | null | undefined, side: 'LEFT' | 'RIGHT', count: number): string[] | null {
  if (!diffHunk || count < 1) return null;
  const skip = side === 'RIGHT' ? '-' : '+';
  const rows = diffHunk
    .replace(/\r\n?/g, '\n')
    .split('\n')
    .filter((r) => !r.startsWith('@@') && !r.startsWith('\\') && !r.startsWith(skip));
  if (rows.length < count) return null;
  return rows.slice(rows.length - count).map((r) => r.slice(1));
}

/**
 * Lines `start`…`line` (1-based, inclusive; `start` defaults to `line`) of a
 * file. Null when the file no longer has them (a thread whose line GitHub
 * could not carry to the head has a null `line`).
 */
export function threadRange(file: readonly string[], start: number | null, line: number | null): string[] | null {
  if (line === null) return null;
  const from = start ?? line;
  if (from < 1 || line > file.length || from > line) return null;
  return file.slice(from - 1, line);
}

/**
 * Whether a suggestion is outdated: the lines it replaces (`original`, as
 * the comment saw them) no longer read the same at the head (`current`,
 * null when the head no longer has them).
 */
export function isOutdated(original: readonly string[], current: readonly string[] | null): boolean {
  return current === null || current.length !== original.length || current.some((l, i) => l !== original[i]);
}

/**
 * The diff a suggestion makes: lines shared at the start and end are
 * context, the original lines between them are removed and the suggested
 * ones added.
 */
export function suggestionDiff(original: readonly string[], suggested: readonly string[]): SuggestionRow[] {
  let head = 0;
  while (head < original.length && head < suggested.length && original[head] === suggested[head]) head++;
  let tail = 0;
  while (
    tail < original.length - head &&
    tail < suggested.length - head &&
    original[original.length - 1 - tail] === suggested[suggested.length - 1 - tail]
  )
    tail++;
  const rows: SuggestionRow[] = [];
  for (let i = 0; i < head; i++) rows.push({ kind: 'context', text: original[i], at: i });
  for (let i = head; i < original.length - tail; i++) rows.push({ kind: 'del', text: original[i], at: i });
  for (let i = head; i < suggested.length - tail; i++) rows.push({ kind: 'add', text: suggested[i], at: i });
  for (let i = original.length - tail; i < original.length; i++) rows.push({ kind: 'context', text: original[i], at: i });
  return rows;
}

/** Where a suggestion's comment sits: its thread's lines and the comment's diff hunk. */
export interface SuggestionTarget {
  side: 'LEFT' | 'RIGHT';
  /** The thread's range at the head (null `line`: GitHub could not carry it there). */
  line: number | null;
  startLine: number | null;
  /** The range when the comment was written. */
  originalLine: number | null;
  originalStartLine: number | null;
  diffHunk?: string;
}

/**
 * What a received suggestion replaces, and whether it is outdated.
 * `original` is the targeted text when the comment was written (from its
 * diff hunk), else the head's text of the thread's lines, else null.
 * `head` is the file at the PR's head, or null while it is unknown. Only a
 * new-side suggestion with a known original can be outdated: its lines are
 * gone from the head, or read differently there.
 */
export function suggestionBasis(t: SuggestionTarget, head: readonly string[] | null): { original: string[] | null; outdated: boolean } {
  const end = t.originalLine ?? t.line;
  const fromHunk = end === null ? null : hunkLines(t.diffHunk, t.side, end - (t.originalStartLine ?? end) + 1);
  // The head's text of the lines: null when gone, undefined when unknown.
  const current = t.side !== 'RIGHT' ? undefined : t.line === null ? null : head === null ? undefined : threadRange(head, t.startLine, t.line);
  return {
    original: fromHunk ?? current ?? null,
    outdated: fromHunk !== null && current !== undefined && isOutdated(fromHunk, current),
  };
}

/**
 * The indentation one Tab adds when editing `lines`: a tab when they indent
 * with tabs, else the smallest space indent among them (2, 3 or 4), else 4
 * spaces (the smallest is a multiple of 4, or nothing is indented).
 */
export function indentUnitOf(lines: readonly string[]): string {
  if (lines.some((l) => l.startsWith('\t'))) return '\t';
  const widths = lines.filter((l) => l.trim()).map((l) => l.length - l.replace(/^ +/, '').length).filter((w) => w > 0);
  const min = Math.min(...widths);
  return ' '.repeat(min >= 2 && min <= 4 ? min : 4);
}

/**
 * Tab and Shift-Tab in a plain text box: indent (outdent) every line the
 * selection `start`…`end` touches by one `unit`, as the code editor does.
 * Returns the new text and selection.
 */
export function indentLines(
  text: string,
  start: number,
  end: number,
  outdent: boolean,
  unit = '    ',
): { text: string; start: number; end: number } {
  const first = text.lastIndexOf('\n', start - 1) + 1;
  // Through the end of the last line touched (a selection ending at a line's start does not touch it).
  const last = end > start && text[end - 1] === '\n' ? end - 1 : end;
  const stop = text.indexOf('\n', last) === -1 ? text.length : text.indexOf('\n', last);
  const lines = text.slice(first, stop).split('\n');
  let shiftStart = 0;
  let shift = 0;
  const changed = lines.map((l, i) => {
    const next = outdent ? outdentLine(l, unit) : unit + l;
    const d = next.length - l.length;
    if (i === 0) shiftStart = d;
    shift += d;
    return next;
  });
  return {
    text: text.slice(0, first) + changed.join('\n') + text.slice(stop),
    start: Math.max(first, start + shiftStart),
    end: Math.max(first, end + shift),
  };
}

/** Columns a tab advances to (CodeMirror's default tab size). */
const TAB_SIZE = 4;

/**
 * A line with its indentation one `unit` narrower, by columns as
 * CodeMirror's indentLess does: the leading whitespace is rewritten (tabs
 * first when the unit is a tab), so a tab unit never strips a single space
 * as if it were a tab.
 */
function outdentLine(line: string, unit: string): string {
  const lead = line.length - line.replace(/^[ \t]+/, '').length;
  let col = 0;
  for (const c of line.slice(0, lead)) col = c === '\t' ? col + TAB_SIZE - (col % TAB_SIZE) : col + 1;
  const width = unit === '\t' ? TAB_SIZE : unit.length;
  const target = Math.max(0, col - width);
  const indent = unit === '\t' ? '\t'.repeat(Math.floor(target / TAB_SIZE)) + ' '.repeat(target % TAB_SIZE) : ' '.repeat(target);
  return indent + line.slice(lead);
}
